import { formatDistance, getProviderType } from "@wags/catalog/pure.ts";
import { ChevronSmallRightIcon } from "@wags/ui/product-icons.tsx";
import { Link } from "@tanstack/react-router";
import type { NearbyProvider } from "../nearby-providers";
import { formatNearbyProviderDetail } from "./nearby-provider-detail";
import { VerifiedProviderBadge } from "@/components/shared/VerifiedProviderBadge";

export function NearbyProvidersSection({
    providers,
    city,
}: {
    providers: NearbyProvider[];
    city: string | null | undefined;
}) {
    return (
        <div className="flex flex-col">
            <h2 className="text-section-title text-doberman-black text-pretty">
                {city
                    ? `Other breeders and shelters near ${city}`
                    : "Other breeders and shelters nearby"}
            </h2>
            <ul className="mt-4">
                {providers.map((provider) => {
                    const distance = formatDistance(
                        provider.distanceMiles,
                        "compact",
                    );
                    return (
                        <li
                            key={provider.id}
                            className="border-t border-doberman-black/10 transition-colors duration-100 last:border-b motion-reduce:transition-none [@media(hover:hover)]:hover:border-transparent [@media(hover:hover)]:[&:hover+li]:border-t-transparent has-[:active]:border-transparent has-[:active]:transition-none [&:has(:active)+li]:border-t-transparent [&:has(:active)+li]:transition-none has-[:focus-visible]:border-transparent has-[:focus-visible]:transition-none [&:has(:focus-visible)+li]:border-t-transparent [&:has(:focus-visible)+li]:transition-none"
                        >
                            <Link
                                to="/provider/$providerId"
                                params={{ providerId: provider.id }}
                                className="flex min-h-11 touch-manipulation items-center gap-4 rounded-nested px-4 py-4 outline-none transition-colors duration-100 active:bg-doberman-black/[0.03] active:transition-none motion-reduce:transition-none md:px-5 [@media(hover:hover)]:hover:bg-doberman-black/[0.02] focus-visible:bg-doberman-black/[0.04] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-doberman-black/60 focus-visible:transition-none"
                            >
                                <span className="min-w-0 flex-1">
                                    <span className="flex min-w-0 items-baseline text-body font-semibold text-doberman-black">
                                        <span className="truncate">
                                            {provider.name}
                                        </span>
                                        <VerifiedProviderBadge
                                            isVerified={provider.isVerified}
                                            providerType={getProviderType(
                                                provider.type,
                                            )}
                                        />
                                    </span>
                                    <span className="block text-body-small text-doberman-black/60">
                                        {formatNearbyProviderDetail(provider)}
                                    </span>
                                </span>
                                {distance && (
                                    <span className="whitespace-nowrap text-body-small font-medium tabular-nums text-doberman-black/60">
                                        {distance}
                                    </span>
                                )}
                                <ChevronSmallRightIcon
                                    aria-hidden
                                    className="size-4 shrink-0 text-doberman-black/50"
                                />
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
